import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postData } from '../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const UserInvitation = () => {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  const alertConfigurations = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  };

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: (userData) => postData(`api/companies/${companySlug}/user_invitations`, { user: userData }),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries(['companyUsers', companySlug]);
        toast.success(result.message, alertConfigurations);
        navigate(`/companies/${companySlug}/users`);
      } else {
        if (result.errors) {
          setErrors(result.errors);
        } else {
          toast.error(result.error || 'Failed to send invitation', alertConfigurations);
        }
      }
    },
    onError: (error) => {
      console.error('Invite user error:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.error || 'Failed to send invitation', alertConfigurations);
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    inviteUserMutation.mutate({ email: email.trim() });
  };

  const handleCancel = () => {
    navigate(`/companies/${companySlug}/users`);
  };

  return (
    <div className="users-management-container">
      <div className="users-page">
        <div className="row jusitfy-content-start mt-5">
          <div className="col-md-6">
            <div className="">
              <h2 className="mb-4" style={{ fontFamily: 'Manrope', fontWeight: '600', color: '#333' }}>Invite User</h2>
              
              <form onSubmit={handleSubmit} className="invite-form">
                <div className="form-group ml-0 ml-md-3">
                  <label htmlFor="email" className="form-label" style={{ 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '0.5rem', 
                    fontFamily: 'Manrope' 
                  }}>
                    Email
                  </label>
                  <div className="input-group" style={{ position: 'relative' }}>
                    <input
                      type="email"
                      className={`form-control invite-input ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email address"
                      disabled={inviteUserMutation.isLoading}
                      autoFocus
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        padding: '0px 0px 0px 50px',
                        fontFamily: 'Manrope',
                        transition: 'border-color 0.2s ease'
                      }}
                    />
                    <span className="input-group-text" style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      zIndex: 10,
                      color: '#6c757d',
                      fontSize: '16px',
                      pointerEvents: 'none'
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-envelope" viewBox="0 0 16 16">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z"/>
                      </svg>
                    </span>
                  </div>
                  {errors.email && (
                    <div className="invalid-feedback">
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="text-center d-flex justify-content-end align-items-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-4 w-fit mr-3"
                    disabled={inviteUserMutation.isLoading}
                    style={{ 
                      backgroundColor: '#6366f1', 
                      borderColor: '#6366f1',
                      fontFamily: 'Manrope',
                      fontWeight: '500'
                    }}
                  >
                    {inviteUserMutation.isLoading ? 'Submitting...' : 'Submit'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={handleCancel}
                    disabled={inviteUserMutation.isLoading}
                    style={{
                      fontFamily: 'Manrope',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInvitation;